// auditpm — Prospects: the outreach database (24k+ bizSAFE-certified companies, imported Jul 2026).
// Read-mostly: search, status counts, per-prospect outreach history, and the do-not-contact switch.
// The outreach ENGINE lives in the send-outreach edge function; this page is the eyes on it.
import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { format, parseISO } from 'date-fns'

const STATUS_META = {
  ready: ['badge-ok', 'Ready'],
  hold_unverified: ['badge-warn', 'Hold (unverified)'],
  engaged: ['badge-info', 'Engaged'],
  converted: ['badge-info', 'Converted'],
  existing_client: ['badge-gray', 'Existing client'],
  not_interested: ['badge-gray', 'Not interested'],
  unsubscribed: ['badge-danger', 'Unsubscribed'],
  bounced: ['badge-danger', 'Bounced'],
}

const PAGE = 50

export default function Prospects() {
  const [counts, setCounts] = useState({})
  const [rows, setRows] = useState([])
  const [outreach, setOutreach] = useState({})           // prospect_id -> [{touch, sent_at}]
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [expFrom, setExpFrom] = useState('')             // expiry-date range filter (inclusive)
  const [expTo, setExpTo] = useState('')
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => { loadCounts() }, [])
  useEffect(() => { const t = setTimeout(loadRows, search ? 300 : 0); return () => clearTimeout(t) }, [search, statusFilter, expFrom, expTo, page])

  async function loadCounts() {
    // One grouped count via a head-less select per status is wasteful; pull all statuses cheaply instead.
    const { data } = await supabase.rpc('prospect_status_counts').select()
    if (data) { setCounts(Object.fromEntries(data.map(r => [r.status, r.n]))); return }
    // Fallback if the RPC doesn't exist: count the big two only.
    const { count: ready } = await supabase.from('prospects').select('id', { count: 'exact', head: true }).eq('status', 'ready')
    const { count: all } = await supabase.from('prospects').select('id', { count: 'exact', head: true })
    setCounts({ ready, _all: all })
  }

  async function loadRows() {
    setLoading(true)
    let q = supabase.from('prospects')
      .select('id, name, uen, email, bizsafe_level, expiry_date, status, do_not_contact', { count: 'exact' })
    if (statusFilter) q = q.eq('status', statusFilter)
    if (expFrom) q = q.gte('expiry_date', expFrom)       // inclusive expiry-date range
    if (expTo) q = q.lte('expiry_date', expTo)
    if (search) q = q.or(`name.ilike.%${search}%,uen.ilike.%${search}%,email.ilike.%${search}%`)
    const { data, count } = await q.order('expiry_date', { ascending: true }).range(page * PAGE, page * PAGE + PAGE - 1)
    setRows(data || [])
    setTotal(count || 0)
    setLoading(false)
    // Outreach history for the visible rows only
    const ids = (data || []).map(r => r.id)
    if (ids.length) {
      const { data: hist } = await supabase.from('prospect_outreach').select('prospect_id, touch, sent_at').in('prospect_id', ids)
      const map = {}
      for (const h of hist || []) { (map[h.prospect_id] = map[h.prospect_id] || []).push(h) }
      setOutreach(map)
    } else setOutreach({})
  }

  async function toggleDnc(r) {
    const next = !r.do_not_contact
    await supabase.from('prospects').update({ do_not_contact: next }).eq('id', r.id)
    setRows(rs => rs.map(x => x.id === r.id ? { ...x, do_not_contact: next } : x))
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE))
  const fmt = d => { try { return format(parseISO(d), 'd MMM yyyy') } catch (e) { return d || '—' } }

  return (
    <div>
      <div className="page-header">
        <h1>Prospects</h1>
        <span style={{ fontSize: 13, color: 'var(--text3)' }}>Outreach runs daily at 9:30am · unsubscribes are automatic</span>
      </div>

      <div className="stat-grid">
        {[['ready', 'Ready for outreach'], ['engaged', 'Engaged'], ['unsubscribed', 'Unsubscribed'], ['converted', 'Won by outreach']].map(([k, l]) => (
          <div key={k} className="stat-card" style={{ cursor: 'pointer', outline: statusFilter === k ? '2px solid var(--brand)' : 'none' }}
            onClick={() => { setStatusFilter(f => f === k ? '' : k); setPage(0) }}>
            <div className="stat-val">{counts[k] ?? 0}</div>
            <div className="stat-lbl">{l}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <input placeholder="Search name, UEN or email…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(0) }} style={{ maxWidth: 280 }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0) }} style={{ width: 'auto' }}>
          <option value="">All statuses</option>
          {Object.keys(STATUS_META).map(s => <option key={s} value={s}>{STATUS_META[s][1]}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text3)' }}>Expiry</span>
          <input type="date" value={expFrom} max={expTo || undefined} onChange={e => { setExpFrom(e.target.value); setPage(0) }} style={{ width: 'auto' }} title="Expiry from" />
          <span style={{ fontSize: 13, color: 'var(--text3)' }}>to</span>
          <input type="date" value={expTo} min={expFrom || undefined} onChange={e => { setExpTo(e.target.value); setPage(0) }} style={{ width: 'auto' }} title="Expiry to" />
          {(expFrom || expTo) && <button className="sm" onClick={() => { setExpFrom(''); setExpTo(''); setPage(0) }}>Clear</button>}
        </div>
        <span style={{ fontSize: 13, color: 'var(--text3)', alignSelf: 'center' }}>{total.toLocaleString()} prospects</span>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Company</th><th>Level</th><th>Expiry</th><th>Status</th><th>Outreach</th><th>Do not contact</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>No prospects found</td></tr>}
              {rows.map(r => {
                const [cls, label] = STATUS_META[r.status] || ['badge-gray', r.status]
                const touches = (outreach[r.id] || []).sort((a, b) => a.touch - b.touch)
                return (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{r.name}</div>
                      <div style={{ fontSize: 13, color: 'var(--text3)' }}>{r.uen || '—'} · {r.email || 'no email'}</div>
                    </td>
                    <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{r.bizsafe_level || '—'}</td>
                    <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmt(r.expiry_date)}</td>
                    <td><span className={`badge ${cls}`}>{label}</span></td>
                    <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                      {touches.length === 0
                        ? <span style={{ color: 'var(--text3)' }}>—</span>
                        : touches.map(t => <span key={t.touch} className="badge badge-info" style={{ marginRight: 4 }} title={fmt(t.sent_at)}>T{t.touch}</span>)}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={!!r.do_not_contact} onChange={() => toggleDnc(r)}
                        title="Tick = never email this company (manual override, wins over everything)" />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', justifyContent: 'center' }}>
        <button className="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
        <span style={{ fontSize: 13, color: 'var(--text2)' }}>Page {page + 1} of {totalPages}</span>
        <button className="sm" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
      </div>
    </div>
  )
}
